/**
 * PATH: packages/email-hub/src/feature/components/AttachmentChip.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-ATTACHMENTCHIP-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/AttachmentChip.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/AttachmentChip.tsx:
 *     thin re-export shim that points back here.
 */

import { useEffect, useRef, useState } from 'react'
import {
  Download,
  Eye,
  Link2,
  Loader2,
} from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import { emailApiFetch } from '../lib/api'
import { formatFileSize } from '../lib/utils'
import { attachmentChipStyle } from '../lib/attachmentIcons'
import type { AttachmentChipStyle } from '../lib/attachmentIcons'
import type { AttachmentMeta } from '@quotech/ui'
import type { EmailAttachment } from '../hooks/useEmails'

interface AttachmentChipProps {
  attachment: EmailAttachment
  onPreview: () => void
  /** Optional — opens the right-click context menu at the event's
   *  coordinates. Parent owns the menu state so only one is open at
   *  a time across all chips in the list. */
  onContextMenu?: (e: React.MouseEvent) => void
  /** Email→Quote ARC § 3.3 (P3) — per-chip download progress
   *  percentage in [0, 100]. When provided AND strictly between 0
   *  and 100 the chip overlays a thin progress bar at the bottom
   *  edge + a small spinner badge over the icon, so the operator
   *  sees the file streaming in. Wired no-op for now; Scope 2A
   *  (OneDrive / SharePoint / Drive auto-scrape, P5/P6 in the ARC)
   *  hooks the real progress feed in. ``null`` / ``undefined`` /
   *  ``>= 100`` render the chip exactly as today. */
  progress?: number | null
}

export default function AttachmentChip({
  attachment,
  onPreview,
  onContextMenu,
  progress,
}: AttachmentChipProps) {
  const { toast } = useToast()
  const [downloading, setDownloading] = useState(false)
  const downloadable = !!attachment.download_url
  const styled = attachmentChipStyle(attachment)
  const meta = styled.meta

  async function handleDownload(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation()
    if ('preventDefault' in e) e.preventDefault()
    if (!attachment.download_url || downloading) return
    setDownloading(true)
    try {
      const res = await emailApiFetch(attachment.download_url)
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status}${text ? `: ${text.slice(0, 120)}` : ''}`)
      }
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = attachment.filename || 'attachment'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Download failed'
      toast(`Could not download ${attachment.filename}: ${msg}`, 'error')
    } finally {
      setDownloading(false)
    }
  }

  // Email→Quote ARC § 3.3 (P3) — render the chip-level progress
  // overlay only when progress is strictly between 0 and 100. ``null``
  // / ``undefined`` / ``0`` / ``>= 100`` collapse to the existing
  // chip layout (so the chip never gains a spinner for a static
  // attachment, and the in-flight indicator vanishes upon completion
  // — symmetric with the operator's "% progress bar... vanishes upon
  // completion" requirement from the original P3 brief).
  const showProgress =
    typeof progress === 'number' && progress > 0 && progress < 100
  const clampedProgress = showProgress ? Math.max(0, Math.min(100, progress as number)) : 0

  return (
    <button
      type="button"
      onClick={() => downloadable && onPreview()}
      onContextMenu={onContextMenu}
      disabled={!downloadable}
      className={cn(
        'group relative flex items-center gap-3 p-2.5 pr-2',
        'bg-background rounded-lg border border-border w-full text-left',
        'transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        downloadable
          ? 'hover:border-primary/50 hover:shadow-md hover:bg-accent/40 cursor-pointer'
          : 'opacity-60 cursor-not-allowed',
      )}
      title={downloadable ? `Click to preview ${attachment.filename}` : 'Attachment unavailable'}
    >
      {/* Icon / thumbnail slot */}
      <ChipIcon attachment={attachment} meta={meta} styled={styled} />
      {showProgress && (
        <span
          aria-hidden
          className="absolute top-1.5 left-1.5 h-3.5 w-3.5 rounded-full bg-primary/15 flex items-center justify-center"
          data-testid="attachment-chip-progress-badge"
        >
          <Loader2 className="h-2.5 w-2.5 animate-spin text-primary" />
        </span>
      )}

      {/* Filename + meta */}
      <div className="min-w-0 flex-1">
        <p
          className="font-medium text-foreground text-sm truncate leading-tight"
          title={attachment.filename}
        >
          {attachment.filename}
        </p>
        {/* One line, always: every segment is nowrap and the row clips
            instead of wrapping — "83.8 KB" must never break across lines
            or push the badge under the icons in a narrow pane. */}
        <p className="text-[11px] text-muted-foreground leading-tight mt-0.5 flex items-center gap-1.5 overflow-hidden">
          <span className={cn('font-medium whitespace-nowrap', styled.labelClass)}>{meta.label}</span>
          {attachment.size > 0 && (
            <>
              <span aria-hidden className="opacity-50">·</span>
              <span className="whitespace-nowrap">{formatFileSize(attachment.size)}</span>
            </>
          )}
          {/* Email→Quote ARC § 3.4 (P4 / Scope 1C) — surface a small
              "linked to QDrive" badge when the attachment has been
              ingested into the canonical FileRecord store. Visual
              hint only - the actual "Open in QDrive" / "Copy QDrive
              link" actions live in the right-click context menu
              (AttachmentContextMenu). Tooltip carries the short
              ref code (e.g. ``QF-A7B3C9``) when one exists so a
              hover answers "what's the short cross-hub ref?". */}
          {attachment.qdrive_file_id && (
            <>
              <span aria-hidden className="opacity-50">·</span>
              <span
                className="inline-flex items-center gap-0.5 text-primary/80 whitespace-nowrap"
                title={
                  attachment.qdrive_ref_code
                    ? `In QDrive · ref ${attachment.qdrive_ref_code} · right-click for actions`
                    : 'In QDrive · right-click for actions'
                }
                data-testid="attachment-chip-qdrive-badge"
              >
                <Link2 className="h-3 w-3" aria-hidden />
                <span className="font-medium">QDrive</span>
              </span>
            </>
          )}
        </p>
      </div>

      {/* Action cluster — preview hint + download. The eye icon only
          appears on hover so the chip stays clean at rest. */}
      <div className="flex items-center gap-0.5 shrink-0">
        {downloadable && (
          <span
            aria-hidden
            className={cn(
              'p-1.5 rounded text-muted-foreground',
              'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
              'transition-opacity',
            )}
            title="Preview"
          >
            <Eye className="h-4 w-4" />
          </span>
        )}
        {downloadable ? (
          <span
            role="button"
            tabIndex={0}
            onClick={handleDownload}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleDownload(e)
            }}
            className={cn(
              'p-1.5 rounded text-muted-foreground',
              'hover:bg-primary/10 hover:text-primary transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            )}
            title={`Download ${attachment.filename}`}
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </span>
        ) : (
          <span className="p-1.5 rounded opacity-40 cursor-not-allowed">
            <Download className="h-4 w-4 text-muted-foreground" />
          </span>
        )}
      </div>
      {showProgress && (
        <span
          aria-hidden
          className="absolute left-0 right-0 bottom-0 h-0.5 bg-primary/10 overflow-hidden rounded-b-lg"
          data-testid="attachment-chip-progress-bar"
        >
          <span
            className="block h-full bg-primary transition-[width] duration-200 ease-out"
            style={{ width: `${clampedProgress}%` }}
            role="progressbar"
            aria-valuenow={Math.round(clampedProgress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Downloading ${attachment.filename}`}
          />
        </span>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// ChipIcon — image attachments fetch a real thumbnail; everything
// else gets a coloured type-icon. The thumbnail uses the existing
// /download/ endpoint and an object URL; revoked on unmount.
// ---------------------------------------------------------------------------

function ChipIcon({
  attachment,
  meta,
  styled,
}: {
  attachment: EmailAttachment
  meta: AttachmentMeta
  styled: AttachmentChipStyle
}) {
  const isImage = meta.kind === 'image'
  const [thumbUrl, setThumbUrl] = useState<string | null>(null)
  const [thumbErrored, setThumbErrored] = useState(false)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isImage || !attachment.download_url) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await emailApiFetch(attachment.download_url!)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        objectUrlRef.current = url
        if (cancelled) {
          URL.revokeObjectURL(url)
          return
        }
        setThumbUrl(url)
      } catch {
        if (!cancelled) setThumbErrored(true)
      }
    })()
    return () => {
      cancelled = true
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [isImage, attachment.download_url])

  if (isImage && thumbUrl && !thumbErrored) {
    return (
      <div className="h-10 w-10 rounded-lg overflow-hidden bg-muted shrink-0 ring-1 ring-border">
        <img
          src={thumbUrl}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
    )
  }

  const Icon = styled.icon
  return (
    <div
      className={cn(
        'h-10 w-10 rounded-lg flex items-center justify-center shrink-0',
        styled.tintClass,
      )}
    >
      <Icon className={cn('h-5 w-5', styled.iconClass)} />
    </div>
  )
}

